import React, { useEffect, useRef, useState } from 'react';
import {
  Camera, FileText, Fingerprint, ImageIcon, MapPin, MoreHorizontal, Ruler, ScrollText,
} from 'lucide-react';
import { urlArchivo } from 'services/arbolesService';
import { datosDeNotas, medida } from 'lib/arboles';
import { fechaHoraCorta, nombreUsuario } from 'lib/auditoria';
import { etiquetaInstalacion } from 'lib/auditoriaInstalaciones';
import Button from 'components/ui/Button';

// Bloque de la ficha: tarjeta con borde, título en Plus Jakarta y acciones.
export function Seccion({ id, titulo, detalle, acciones, children, className = '' }) {
  return (
    <section id={id} aria-labelledby={`${id}-t`} className={`scroll-mt-24 rounded-xl border border-border bg-card ${className}`}>
      <header className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 border-b border-border px-4 py-3 sm:px-5">
        <h2 id={`${id}-t`} className="font-heading text-base font-semibold text-foreground">
          {titulo}
          {detalle != null && <span className="ml-2 text-sm font-medium text-muted-foreground tabular-nums">{detalle}</span>}
        </h2>
        {acciones}
      </header>
      <div className="px-4 py-4 sm:px-5">{children}</div>
    </section>
  );
}

// Abre un archivo del bucket con URL firmada (se pide al tocar, dura 1 hora).
export function BotonArchivo({ path, children }) {
  const [estado, setEstado] = useState('');
  const abrir = async () => {
    setEstado('cargando');
    const url = await urlArchivo(path);
    if (!url) { setEstado('error'); return; }
    setEstado('');
    window.open(url, '_blank', 'noopener');
  };
  return (
    <span className="inline-flex items-center gap-2">
      <Button type="button" variant="outline" size="xs" onClick={abrir} loading={estado === 'cargando'}>
        {estado !== 'cargando' && <FileText size={13} className="mr-1" aria-hidden="true" />}{children}
      </Button>
      {estado === 'error' && <span className="text-xs text-error">No se pudo abrir el archivo.</span>}
    </span>
  );
}

// Menú "Más" con las acciones secundarias (editar, dar de baja). Cierra con
// Escape, al tocar fuera o al elegir.
export function MenuMas({ opciones }) {
  const [abierto, setAbierto] = useState(false);
  const caja = useRef(null);
  const boton = useRef(null);
  useEffect(() => {
    if (!abierto) return undefined;
    const fuera = (e) => { if (!caja.current?.contains(e.target)) setAbierto(false); };
    const tecla = (e) => { if (e.key === 'Escape') { setAbierto(false); boton.current?.focus(); } };
    document.addEventListener('mousedown', fuera);
    document.addEventListener('touchstart', fuera);
    document.addEventListener('keydown', tecla);
    return () => {
      document.removeEventListener('mousedown', fuera);
      document.removeEventListener('touchstart', fuera);
      document.removeEventListener('keydown', tecla);
    };
  }, [abierto]);
  if (!opciones.length) return null;
  return (
    <div ref={caja} className="relative">
      <Button ref={boton} type="button" variant="outline" size="sm" aria-haspopup="menu" aria-expanded={abierto}
        aria-label="Más acciones" onClick={() => setAbierto((v) => !v)} className="px-2.5 hover:bg-muted hover:text-foreground aria-expanded:bg-muted">
        <MoreHorizontal size={18} aria-hidden="true" />
      </Button>
      {abierto && (
        <div role="menu" className="absolute right-0 z-20 mt-1.5 min-w-[11rem] overflow-hidden rounded-lg border border-border bg-card py-1 shadow-lg">
          {opciones.map((o) => (
            <button key={o.id} type="button" role="menuitem"
              onClick={() => { setAbierto(false); o.onClick(); }}
              className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted focus-visible:bg-muted focus-visible:outline-none ${
                o.peligro ? 'text-error' : 'text-foreground'}`}>
              <o.icono size={15} aria-hidden="true" className={o.peligro ? '' : 'text-muted-foreground'} />{o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Fila etiqueta/valor de un grupo. Sin dato se ve atenuado, no desaparece.
export function Dato({ label, children, mono, ancho }) {
  const vacio = children === null || children === undefined || children === '';
  return (
    <div className={`min-w-0 sm:py-2 sm:first:pt-0 sm:last:pb-0 ${ancho ? 'col-span-2 sm:col-span-1' : ''}`}>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className={`mt-0.5 break-words text-[15px] font-medium leading-snug ${vacio ? 'font-normal text-muted-foreground/70' : 'text-foreground'} ${mono && !vacio ? 'tabular-nums' : ''}`}>
        {vacio ? 'Sin dato' : children}
      </dd>
    </div>
  );
}

export function Grupo({ titulo, icono: Icono, children }) {
  return (
    <div className="min-w-0">
      <h3 className="mb-2 flex items-center gap-1.5 text-[13px] font-semibold text-primary">
        <Icono size={14} aria-hidden="true" />{titulo}
      </h3>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-3 sm:block sm:divide-y sm:divide-border/70">{children}</dl>
    </div>
  );
}

export function Foto({ path, alt = 'Foto del árbol' }) {
  const [url, setUrl] = useState(null);
  const [falla, setFalla] = useState(false);
  useEffect(() => {
    let vivo = true;
    setUrl(null);
    setFalla(false);
    if (path) urlArchivo(path).then((u) => { if (vivo) { setUrl(u); setFalla(!u); } });
    return () => { vivo = false; };
  }, [path]);
  if (!path) return null;
  return url ? (
    <a href={url} target="_blank" rel="noreferrer" className="block">
      <img src={url} alt={alt} className="aspect-[4/3] w-full rounded-lg object-cover ring-1 ring-black/5" />
    </a>
  ) : (
    <div className="flex aspect-[4/3] w-full items-center justify-center gap-1.5 rounded-lg bg-muted text-xs text-muted-foreground">
      <ImageIcon size={16} aria-hidden="true" />{falla ? 'Foto no disponible' : 'Cargando foto…'}
    </div>
  );
}

// Botón compacto para agregar o cambiar la foto (va en el encabezado).
export function BotonFoto({ tieneFoto, onElegir, subiendo, etiqueta = 'Foto del árbol' }) {
  const input = useRef(null);
  return (
    <>
      <Button type="button" variant="ghost" size="xs" onClick={() => input.current?.click()} loading={subiendo}
        className="text-muted-foreground hover:text-foreground">
        {!subiendo && <Camera size={14} className="mr-1" aria-hidden="true" />}{tieneFoto ? 'Cambiar foto' : 'Agregar foto'}
      </Button>
      <input ref={input} type="file" accept="image/*" className="sr-only" aria-label={etiqueta}
        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) onElegir(f); }} />
    </>
  );
}

// Datos del árbol en cuatro grupos. Los pares "Clave: valor" de las notas se
// muestran en su lugar y el resto queda como observaciones.
export function DatosArbol({ arbol, nombres, errorFoto }) {
  const { datos, observaciones } = datosDeNotas(arbol.notas);
  const especie = arbol.especie || datos.nombre_cientifico || null;
  const mapa = arbol.lat !== null && arbol.lat !== undefined && arbol.lng !== null && arbol.lng !== undefined
    ? `https://www.google.com/maps?q=${arbol.lat},${arbol.lng}` : null;
  const conFoto = !!arbol.foto_path;
  return (
    <div>
      {errorFoto && <p role="alert" className="mb-3 rounded-lg bg-error/10 px-2.5 py-2 text-xs text-error">{errorFoto}</p>}
      <div className={conFoto ? 'grid gap-5 lg:grid-cols-[minmax(0,1fr)_220px]' : ''}>
        <div className="grid gap-x-6 gap-y-5 sm:grid-cols-2 lg:grid-cols-4">
          <Grupo titulo="Identificación" icono={Fingerprint}>
            <Dato label="Código">{arbol.codigo}</Dato>
            <Dato label="Nombre común">{arbol.nombre_comun}</Dato>
            <Dato label="Especie">{especie && <i>{especie}</i>}</Dato>
            <Dato label="Familia">{datos.familia}</Dato>
          </Grupo>
          <Grupo titulo="Medidas" icono={Ruler}>
            <Dato label="DAP (diámetro del tronco)" mono>{medida(arbol.dap_cm, 'cm')}</Dato>
            <Dato label="Altura del árbol" mono>{medida(arbol.altura_m, 'm')}</Dato>
            <Dato label="Altura de la plataforma" mono>{datos.altura_plataforma}</Dato>
          </Grupo>
          <Grupo titulo="Ubicación" icono={MapPin}>
            <Dato label="Qué sostiene">{arbol.sostiene}</Dato>
            <Dato label="Pendiente del terreno" mono>{datos.pendiente}</Dato>
            <Dato label="Coordenadas" mono>
              {mapa && (
                <a href={mapa} target="_blank" rel="noreferrer"
                  className="text-primary underline decoration-primary/30 underline-offset-2 hover:decoration-primary">
                  {arbol.lat}, {arbol.lng}
                </a>
              )}
            </Dato>
            <Dato label="CRTM05" mono>{datos.crtm05}</Dato>
            {arbol.ubicacion && <Dato label="Sector" ancho>{arbol.ubicacion}</Dato>}
          </Grupo>
          <Grupo titulo="Registro" icono={ScrollText}>
            <Dato label="Instalación">{etiquetaInstalacion(arbol.instalacion)}</Dato>
            <Dato label="Registrado">
              {arbol.created_at && (
                <>
                  {fechaHoraCorta(arbol.created_at)}
                  {arbol.created_by && <span className="block text-xs font-normal text-muted-foreground">{nombreUsuario(arbol.created_by, nombres)}</span>}
                </>
              )}
            </Dato>
          </Grupo>
        </div>
        {conFoto && <Foto path={arbol.foto_path} />}
      </div>
      {observaciones && (
        <div className="mt-5 border-t border-border pt-4">
          <h3 className="mb-1.5 text-[13px] font-semibold text-primary">Observaciones</h3>
          <p className="max-w-prose whitespace-pre-line text-sm leading-relaxed text-foreground">{observaciones}</p>
        </div>
      )}
    </div>
  );
}
